import { useState, useEffect, useCallback, createContext, useContext } from "react";
import { Outlet, useNavigate, useLocation, Link } from "react-router-dom";
import { API } from "@/App";
import { toast } from "sonner";
import {
  Flag,
  LayoutDashboard,
  Users,
  Clock,
  Trophy,
  Medal,
  UserCog,
  Settings,
  Globe,
  LogOut,
  Menu,
  X,
  Layers,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

// Club context for child pages
export const ClubContext = createContext(null);
export const useClub = () => useContext(ClubContext);

const NAV_ITEMS = [
  { path: "/club", label: "Dashboard", icon: LayoutDashboard, exact: true },
  { path: "/club/players", label: "Jugadores", icon: Users },
  { path: "/club/tee-times", label: "Tee Times", icon: Clock },
  { path: "/club/categories", label: "Categorias", icon: Layers },
  { path: "/club/competitions", label: "Torneos", icon: Medal },
  { path: "/club/rankings", label: "Ranking", icon: Trophy },
  { path: "/club/team", label: "Usuario del club", icon: UserCog },
  { path: "/club/landing", label: "Pagina Publica", icon: Globe },
  { path: "/club/settings", label: "Configuracion", icon: Settings },
];

export default function ClubLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [clubUser, setClubUser] = useState(null);
  const [club, setClub] = useState(null);
  const [checking, setChecking] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  const verify = useCallback(async () => {
    try {
      const stored = localStorage.getItem("cadii_club_user");
      if (!stored) {
        navigate("/club/login", { replace: true });
        return;
      }

      const res = await fetch(`${API}/club/dashboard`, {
        credentials: "include",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("cadii_club_token") || ""}`,
        },
      });

      if (!res.ok) {
        localStorage.removeItem("cadii_club_user");
        localStorage.removeItem("cadii_club_token");
        navigate("/club/login", { replace: true });
        return;
      }

      const data = await res.json();
      setClubUser(data.user);
      setClub(data.club);

      // Check if password needs to be changed (first access)
      if (!data.user.password_changed) {
        setShowPasswordModal(true);
      }
    } catch {
      navigate("/club/login", { replace: true });
    } finally {
      setChecking(false);
    }
  }, [navigate]);

  useEffect(() => { verify(); }, [verify]);

  const handleLogout = () => {
    localStorage.removeItem("cadii_club_user");
    localStorage.removeItem("cadii_club_token");
    toast.success("Sesion cerrada");
    navigate("/club/login", { replace: true });
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9FAFB]">
        <div className="w-10 h-10 border-4 border-[#1B4D3E] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const isActive = (item) =>
    item.exact ? location.pathname === item.path : location.pathname.startsWith(item.path);

  return (
    <ClubContext.Provider value={{ club, clubUser, setClub, setClubUser }}>
      <div className="min-h-screen bg-[#F9FAFB] flex" style={{ fontFamily: "Outfit, sans-serif" }}>
        {/* Sidebar — desktop */}
        <aside className="hidden lg:flex flex-col w-64 bg-white border-r border-gray-100 fixed inset-y-0 z-30">
          <div className="flex items-center gap-2.5 px-6 h-16 border-b border-gray-100">
            {club?.logo_url ? (
              <img src={club.logo_url} alt={club.name} className="w-7 h-7 rounded-lg object-cover" />
            ) : (
              <Flag className="w-6 h-6 text-[#1B4D3E]" strokeWidth={1.5} />
            )}
            <span className="text-base font-bold text-[#0F172A] truncate">{club?.short_name || club?.name || "Club"}</span>
          </div>

          <nav className="flex-1 px-3 py-4 space-y-1" data-testid="club-sidebar">
            {NAV_ITEMS.map((item) => {
              const Icon = item.icon;
              const active = isActive(item);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    active
                      ? "bg-[#1B4D3E]/10 text-[#1B4D3E]"
                      : "text-[#64748B] hover:bg-gray-50 hover:text-[#0F172A]"
                  }`}
                  data-testid={`club-nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                >
                  <Icon className="w-5 h-5" strokeWidth={1.5} />
                  {item.label}
                </Link>
              );
            })}
          </nav>

          <div className="p-3 border-t border-gray-100">
            <div className="flex items-center gap-3 px-3 py-2 mb-2">
              <div className="w-8 h-8 rounded-full bg-[#1B4D3E] flex items-center justify-center text-white text-xs font-bold">
                {clubUser?.name?.[0] || "M"}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[#0F172A] truncate">{clubUser?.name}</p>
                <p className="text-xs text-[#64748B] truncate">{clubUser?.email}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-red-500 hover:bg-red-50 w-full transition-colors"
              data-testid="club-logout-btn"
            >
              <LogOut className="w-5 h-5" strokeWidth={1.5} />
              Cerrar sesion
            </button>
          </div>
        </aside>

        {/* Mobile header */}
        <header className="lg:hidden fixed top-0 inset-x-0 h-14 bg-white border-b border-gray-100 z-40 flex items-center px-4 justify-between">
          <div className="flex items-center gap-2">
            {club?.logo_url ? (
              <img src={club.logo_url} alt={club.name} className="w-6 h-6 rounded-lg object-cover" />
            ) : (
              <Flag className="w-5 h-5 text-[#1B4D3E]" />
            )}
            <span className="font-bold text-[#0F172A] truncate text-sm">{club?.short_name || club?.name || "Club"}</span>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setSidebarOpen(!sidebarOpen)} data-testid="club-menu-btn">
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
        </header>

        {sidebarOpen && (
          <div className="lg:hidden fixed inset-0 z-30 bg-black/40" onClick={() => setSidebarOpen(false)}>
            <aside className="w-64 bg-white h-full pt-16 shadow-xl" onClick={(e) => e.stopPropagation()}>
              <nav className="px-3 py-4 space-y-1">
                {NAV_ITEMS.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item);
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setSidebarOpen(false)}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                        active
                          ? "bg-[#1B4D3E]/10 text-[#1B4D3E]"
                          : "text-[#64748B] hover:bg-gray-50 hover:text-[#0F172A]"
                      }`}
                    >
                      <Icon className="w-5 h-5" strokeWidth={1.5} />
                      {item.label}
                    </Link>
                  );
                })}
              </nav>
              <div className="p-3 border-t border-gray-100 mt-4">
                <button onClick={handleLogout} className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-red-500 hover:bg-red-50 w-full">
                  <LogOut className="w-5 h-5" strokeWidth={1.5} />
                  Cerrar sesion
                </button>
              </div>
            </aside>
          </div>
        )}

        {/* Main content */}
        <main className="flex-1 lg:ml-64 pt-14 lg:pt-0">
          <Outlet />
        </main>

        {/* First access password change modal */}
        {showPasswordModal && (
          <ChangePasswordModal
            onDone={() => setShowPasswordModal(false)}
          />
        )}
      </div>
    </ClubContext.Provider>
  );
}


function ChangePasswordModal({ onDone }) {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [saving, setSaving] = useState(false);

  const handleChange = async () => {
    if (!newPassword || newPassword.length < 6) {
      return toast.error("La contrasena debe tener al menos 6 caracteres");
    }
    if (newPassword !== confirmPassword) {
      return toast.error("Las contrasenas no coinciden");
    }

    setSaving(true);
    try {
      const res = await fetch(`${API}/club/auth/change-password`, {
        method: "PUT",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("cadii_club_token") || ""}`,
        },
        body: JSON.stringify({ new_password: newPassword }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Error");
      }
      toast.success("Contrasena actualizada");
      onDone();
    } catch (err) {
      toast.error(typeof err.message === "string" ? err.message : "Error al cambiar contrasena");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" data-testid="change-password-modal">
        <div className="bg-[#1B4D3E] px-6 py-5 text-white rounded-t-2xl">
          <h2 className="text-lg font-bold">Bienvenido a tu Cadiihouse</h2>
          <p className="text-sm text-white/70 mt-0.5">Cambia tu contrasena temporal para continuar</p>
        </div>
        <div className="p-6 space-y-4">
          <div className="space-y-2">
            <Label className="text-sm font-medium">Nueva Contrasena</Label>
            <Input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Min. 6 caracteres"
              data-testid="new-password-input"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-medium">Confirmar Contrasena</Label>
            <Input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Repite la contrasena"
              data-testid="confirm-password-input"
            />
          </div>
        </div>
        <div className="px-6 pb-6 flex gap-3">
          <Button variant="outline" className="flex-1" onClick={onDone}>Omitir</Button>
          <Button
            className="flex-1 bg-[#1B4D3E] hover:bg-[#163F33] text-white"
            onClick={handleChange}
            disabled={saving}
            data-testid="save-password-btn"
          >
            {saving ? "Guardando..." : "Cambiar Contrasena"}
          </Button>
        </div>
      </div>
    </div>
  );
}
